import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PiArchiveDuotone,
  PiArrowLeftDuotone,
  PiCalendarDuotone,
  PiListBulletsDuotone,
  PiPencilSimpleDuotone,
  PiTrashDuotone,
  PiWarningDuotone,
  PiCheckCircleDuotone,
  PiSwordDuotone,
  PiArrowRightDuotone,
} from 'react-icons/pi';
import { getEvent, getEventChallenges, updateEventResult, archiveEvent, deleteEvent } from '../api/eventService';
import { ConfirmModal } from '../shared/common/Modal';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import Badge from '../shared/common/Badge';
import Input from '../shared/common/Input';
import { SkeletonCard } from '../shared/common/Skeleton';
import EmptyState from '../shared/common/EmptyState';
import { getEventCategoryLabel } from '../models/eventCategories';
import { buildRoomSelectionPathForEvent } from '../shared/utils/challengeCreationFlow';
import {
  formatEventResultBreakdown,
  formatMainOutcome,
  outcomeCodeLabel,
  outcomeCodesForScore,
  validateMainOutcome,
} from '../shared/business/scoring/mainOutcomeHelpers';

/** Форматирует ISO-дату в читаемый вид */
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const STATUS_LABELS = { open: 'приём ставок', closed: 'ждём результат', resolved: 'завершён' };
const STATUS_MAP = { open: 'open', closed: 'closed', resolved: 'resolved' };

const resolveStatus = (event) => (event?.result ? 'resolved' : 'open');
const formatChallengeStatus = (status) => STATUS_LABELS[status?.toLowerCase?.()] || STATUS_LABELS.open;
const challengeBadgeStatus = (status) => STATUS_MAP[status?.toLowerCase?.()] || STATUS_MAP.open;

const formatDateRange = (event) => {
  const start = formatDate(event?.startDate || event?.eventDate || event?.date);
  const end = formatDate(event?.endDate);
  if (start && end) return `${start} — ${end}`;
  return start || end;
};

const isFutureEvent = (event) => {
  const startDate = event?.startDate || event?.eventDate || event?.date;
  if (!startDate) return false;
  return new Date(startDate).getTime() > Date.now();
};

const buildChallengeViewPath = (challenge) => (
  challenge?.leagueId
    ? `/rooms/${challenge.roomId}/leagues/${challenge.leagueId}/challenges/${challenge.id}`
    : `/rooms/${challenge.roomId}/challenges/${challenge.id}`
);

export default function EventViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [resultError, setResultError] = useState('');
  const [resultSuccess, setResultSuccess] = useState('');
  const [resultLoading, setResultLoading] = useState(false);
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  const [outcomeCode, setOutcomeCode] = useState('');
  const [scoreErrors, setScoreErrors] = useState({});
  const [eventChallenges, setEventChallenges] = useState([]);
  const [eventChallengesLoading, setEventChallengesLoading] = useState(false);
  const [eventChallengesError, setEventChallengesError] = useState('');
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cascadeDeleteConfirmOpen, setCascadeDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    document.title = 'BetMate — Событие';
    setLoading(true);
    getEvent(id)
      .then((data) => {
        setEvent(data);
        setResult(data.result || '');
        if (data.mainResult) {
          setHomeScore(data.mainResult.homeScore != null ? String(data.mainResult.homeScore) : '');
          setAwayScore(data.mainResult.awayScore != null ? String(data.mainResult.awayScore) : '');
          setOutcomeCode(data.mainResult.outcomeCode || '');
        }
        document.title = `BetMate — ${data.title}`;
      })
      .catch(() => setError('Не удалось загрузить событие'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    setEventChallengesLoading(true);
    setEventChallengesError('');
    getEventChallenges(id)
      .then(setEventChallenges)
      .catch(() => setEventChallengesError('Не удалось загрузить созданные пари'))
      .finally(() => setEventChallengesLoading(false));
  }, [id]);

  const isScoreBased = Boolean(event?.drawPolicy);
  const isArchived = Boolean(event?.archivedAt);

  const handleArchive = async () => {
    setArchiveConfirmOpen(false);
    try {
      const updated = await archiveEvent(id);
      setEvent(updated);
    } catch (err) {
      setError(err?.response?.data?.message || 'Не удалось архивировать событие');
    }
  };

  const handleDelete = async () => {
    setDeleteConfirmOpen(false);
    setCascadeDeleteConfirmOpen(false);
    try {
      await deleteEvent(id, event.hasChallenges);
      navigate('/events');
    } catch (err) {
      setError(err?.response?.data?.message || 'Не удалось удалить событие');
    }
  };

  const handleResultSubmit = async (e) => {
    e.preventDefault();
    if (isScoreBased) {
      await submitStructuredResult();
    } else {
      await submitLegacyResult();
    }
  };

  const submitLegacyResult = async () => {
    const normalizedResult = result.trim();
    if (!normalizedResult) {
      setResultError('Укажи официальный результат события');
      setResultSuccess('');
      return;
    }

    setResultLoading(true);
    setResultError('');
    setResultSuccess('');
    try {
      const updated = await updateEventResult(id, normalizedResult);
      setEvent(updated);
      setResult(updated.result || normalizedResult);
setResultSuccess('Результат сохранён. Связанные пари будут завершены автоматически.');
    } catch (err) {
      setResultError(err?.response?.data?.message || 'Не удалось сохранить результат события');
    } finally {
      setResultLoading(false);
    }
  };

  const submitStructuredResult = async () => {
    const validation = validateMainOutcome(homeScore, awayScore, outcomeCode, event.drawPolicy);
    if (!validation.valid) {
      setScoreErrors(validation.errors);
      setResultError('');
      setResultSuccess('');
      return;
    }
    setScoreErrors({});

    const mainResult = {
      homeScore: Number(homeScore),
      awayScore: Number(awayScore),
      outcomeCode,
    };

    setResultLoading(true);
    setResultError('');
    setResultSuccess('');
    try {
      const updated = await updateEventResult(id, null, mainResult);
      setEvent(updated);
      setResult(updated.result || '');
      if (updated.mainResult) {
        setHomeScore(String(updated.mainResult.homeScore ?? ''));
        setAwayScore(String(updated.mainResult.awayScore ?? ''));
        setOutcomeCode(updated.mainResult.outcomeCode || '');
      }
setResultSuccess('Результат сохранён. Связанные пари будут завершены автоматически.');
    } catch (err) {
      setResultError(err?.response?.data?.message || 'Не удалось сохранить результат события');
    } finally {
      setResultLoading(false);
    }
  };

  // ===================================================================================================================
  // = Implementation
  // ===================================================================================================================

  if (loading) {
    return (
      <div className="bm-animate-in" style={{ maxWidth: 720 }}>
        <SkeletonCard />
        <div style={{ marginTop: 'var(--bm-space-lg)' }}>
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <EmptyState
        icon={<PiWarningDuotone size={40} />}
        title="Событие не найдено"
        description={error || 'Возможно, оно было удалено или ещё не создано'}
        action={
          <Button
            variant="ghost"
            icon={<PiArrowLeftDuotone size={16} />}
            onClick={() => navigate('/events')}
          >
            К списку событий
          </Button>
        }
      />
    );
  }

  const formattedDate = formatDateRange(event);
  const outcomes = event.outcomes || [];
  const eventStatus = resolveStatus(event);
  const categoryLabel = getEventCategoryLabel(event);
  const canCreateChallenge = isFutureEvent(event);

  return (
    <div className="bm-animate-in" style={{ maxWidth: 720 }}>
      {/* Кнопка назад */}
      <Button
        variant="ghost"
        size="sm"
        icon={<PiArrowLeftDuotone size={16} />}
        onClick={() => navigate('/events')}
        style={{ marginBottom: 'var(--bm-space-lg)' }}
      >
        Назад к событиям
      </Button>

      {/* Hero-блок события */}
      <Card variant="glass" style={{ marginBottom: 'var(--bm-space-lg)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
          <div>
            <h1 className="bm-h2" style={{ marginBottom: 'var(--bm-space-sm)' }}>
              {event.title}
            </h1>
            {event.description && (
              <p className="bm-text-secondary" style={{ marginBottom: 'var(--bm-space-md)', lineHeight: 1.6 }}>
                {event.description}
              </p>
            )}
            {formattedDate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <PiCalendarDuotone size={15} style={{ color: 'var(--bm-text-muted)', flexShrink: 0 }} />
                <span className="bm-text-sm bm-text-secondary">{formattedDate}</span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--bm-space-sm)', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--bm-space-sm)', flexWrap: 'wrap' }}>
              <Badge status="primary">{categoryLabel}</Badge>
              <Badge status={eventStatus}>
                {eventStatus === 'resolved' ? 'результат указан' : 'ожидает результата'}
              </Badge>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--bm-space-sm)', flexWrap: 'wrap' }}>
              {canCreateChallenge && (
                <Button
                  variant="primary"
                  size="sm"
                  icon={<PiSwordDuotone size={14} />}
                  onClick={() => navigate(buildRoomSelectionPathForEvent(event.id))}
                >
Создать пари
                  </Button>
              )}
              {event.editable && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<PiPencilSimpleDuotone size={14} />}
                  onClick={() => navigate(`/events/${event.id}/edit`)}
                >
                  Редактировать
                </Button>
              )}
              {!isArchived && event.archivable && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<PiArchiveDuotone size={14} />}
                  onClick={() => setArchiveConfirmOpen(true)}
                >
                  Архивировать
                </Button>
              )}
              {!isArchived && event.deletable && event.source === 'MANUAL' && !event.hasChallenges && (
                <Button
                  variant="danger"
                  size="sm"
                  icon={<PiTrashDuotone size={14} />}
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  Удалить
                </Button>
              )}
              {!isArchived && event.deletable && event.source === 'MANUAL' && event.hasChallenges && (
                <Button
                  variant="danger"
                  size="sm"
                  icon={<PiTrashDuotone size={14} />}
                  onClick={() => setCascadeDeleteConfirmOpen(true)}
                >
                  Удалить
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Результат события */}
      <Card style={{ marginBottom: 'var(--bm-space-lg)' }}>
        <h2
          className="bm-h4"
          style={{
            marginBottom: 'var(--bm-space-md)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <PiCheckCircleDuotone size={18} style={{ color: 'var(--bm-accent-green)' }} />
          Официальный результат
        </h2>

        {(event.result || event.mainResult || event.resultBreakdown) && (
          <EventResultSummary event={event} />
        )}

        {event.resultEditable ? (
          isScoreBased ? (
            <StructuredResultForm
              drawPolicy={event.drawPolicy}
              homeScore={homeScore}
              awayScore={awayScore}
              outcomeCode={outcomeCode}
              onHomeScoreChange={setHomeScore}
              onAwayScoreChange={setAwayScore}
              onOutcomeCodeChange={setOutcomeCode}
              errors={scoreErrors}
              resultError={resultError}
              resultSuccess={resultSuccess}
              loading={resultLoading}
              onSubmit={handleResultSubmit}
            />
          ) : (
            <form onSubmit={handleResultSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
              {(outcomes.length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {outcomes.map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      className={`bm-btn bm-btn--sm ${result === chip ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
                      onClick={() => setResult(chip)}
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              )}
              <Input
                label="Результат события"
                type="text"
                value={result}
                onChange={(e) => setResult(e.target.value)}
                placeholder={outcomes.length > 0 ? 'Выбери исход выше или введи точный результат' : 'Например: Победа команды А'}
                error={resultError}
              />
              {resultSuccess && (
                <div style={{ color: 'var(--bm-accent-green)', fontWeight: 600 }}>
                  {resultSuccess}
                </div>
              )}
              <Button
                type="submit"
                variant="success"
                loading={resultLoading}
                icon={<PiCheckCircleDuotone size={16} />}
              >
Сохранить результат и завершить пари
              </Button>
            </form>
          )
        ) : (
          <p className="bm-text-secondary">
            Результат может указать только создатель ручного события.
            Редактирование параметров события недоступно после старта.
          </p>
        )}
      </Card>

      {/* Созданные пари */}
      <Card style={{ marginBottom: 'var(--bm-space-lg)' }}>
        <h2
          className="bm-h4"
          style={{
            marginBottom: 'var(--bm-space-md)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <PiSwordDuotone size={18} style={{ color: 'var(--bm-accent-primary)' }} />
          Пари по событию
        </h2>

        {eventChallengesLoading ? (
          <SkeletonCard />
        ) : eventChallengesError ? (
          <EmptyState
            icon={<PiWarningDuotone size={32} />}
            title="Пари не загрузились"
            description={eventChallengesError}
          />
        ) : eventChallenges.length === 0 ? (
          <EmptyState
            title="Доступных пари пока нет"
            description="Здесь появятся пари по этому событию из комнат, где ты состоишь."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-sm)' }}>
            {eventChallenges.map((challenge) => (
              <button
                key={challenge.id}
                type="button"
                className="bm-event-outcome"
                onClick={() => navigate(buildChallengeViewPath(challenge))}
                style={{ cursor: 'pointer', textAlign: 'left', width: '100%' }}
              >
                <span className="bm-event-outcome__index">
                  <PiSwordDuotone size={16} />
                </span>
                <span className="bm-event-outcome__label" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span>{challenge.title || event.title}</span>
                    <Badge status={challengeBadgeStatus(challenge.status)}>{formatChallengeStatus(challenge.status)}</Badge>
                  </span>
                  <span className="bm-text-sm bm-text-secondary">
                    Комната: {challenge.roomName || `#${challenge.roomId}`}
                    {challenge.betDeadline ? ` · Ставки до ${formatDate(challenge.betDeadline)}` : ''}
                  </span>
                </span>
                <PiArrowRightDuotone size={18} style={{ color: 'var(--bm-text-muted)', flexShrink: 0 }} />
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Исходы события */}
      <Card>
        <h2
          className="bm-h4"
          style={{
            marginBottom: 'var(--bm-space-md)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <PiListBulletsDuotone size={18} style={{ color: 'var(--bm-accent-primary)' }} />
          Исходы события
        </h2>

        {outcomes.length === 0 ? (
          <EmptyState
            title="Исходы не заданы"
            description="Для этого события ещё не добавлены варианты исходов"
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-sm)' }}>
            {outcomes.map((outcome, idx) => (
              <div key={idx} className="bm-event-outcome">
                <span className="bm-event-outcome__index">{idx + 1}</span>
                <span className="bm-event-outcome__label">{outcome}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ConfirmModal
        isOpen={archiveConfirmOpen}
        onConfirm={handleArchive}
        onClose={() => setArchiveConfirmOpen(false)}
        title="Архивировать событие?"
        description="Активные пари не будут затронуты."
        confirmLabel="Архивировать"
        confirmVariant="secondary"
      />
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Удалить событие?"
        message="Это действие необратимо. Событие будет удалено навсегда."
        confirmLabel="Удалить"
      />
      <ConfirmModal
        isOpen={cascadeDeleteConfirmOpen}
        onConfirm={handleDelete}
        onClose={() => setCascadeDeleteConfirmOpen(false)}
title="Удалить событие с пари?"
        message="У этого события есть пари. При удалении все связанные пари и ставки будут удалены каскадно. Это действие необратимо."
        confirmLabel="Удалить всё"
      />
    </div>
  );
}

// ===================================================================================================================
// = Structured результат: счёт + OutcomeCode по DrawPolicy
// ===================================================================================================================

function EventResultSummary({ event }) {
  const breakdownLines = formatEventResultBreakdown(event.resultBreakdown);
  const primaryResult = formatMainOutcome(event.mainResult) || event.result;

  return (
    <div style={{ marginBottom: 'var(--bm-space-md)' }}>
      {primaryResult && (
        <div style={{ color: 'var(--bm-accent-green)', fontWeight: 700, marginBottom: 8 }}>
          {primaryResult}
        </div>
      )}
      {breakdownLines.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {breakdownLines.map((line) => (
            <div key={line.label} className="bm-text-sm bm-text-secondary">
              {line.label}: <strong>{line.value}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StructuredResultForm({
  drawPolicy,
  homeScore,
  awayScore,
  outcomeCode,
  onHomeScoreChange,
  onAwayScoreChange,
  onOutcomeCodeChange,
  errors,
  resultError,
  resultSuccess,
  loading,
  onSubmit,
}) {
  const allowedCodes = outcomeCodesForScore(homeScore, awayScore, drawPolicy);
  const outcomeCodeOptions = allowedCodes.map((code) => ({ value: code, label: outcomeCodeLabel(code) }));

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
      <div style={{ display: 'flex', gap: 'var(--bm-space-sm)' }}>
        <Input
          label="Счёт 1-й стороны *"
          type="number"
          min={0}
          value={homeScore}
          onChange={(e) => onHomeScoreChange(e.target.value)}
          error={errors.homeScore}
          style={{ flex: 1 }}
        />
        <Input
          label="Счёт 2-й стороны *"
          type="number"
          min={0}
          value={awayScore}
          onChange={(e) => onAwayScoreChange(e.target.value)}
          error={errors.awayScore}
          style={{ flex: 1 }}
        />
      </div>
      <div>
        <label className="bm-label">Итоговый исход *</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {outcomeCodeOptions.length === 0 ? (
            <span className="bm-text-secondary bm-text-sm">
              Укажи допустимый счёт, чтобы выбрать исход.
            </span>
          ) : (
            outcomeCodeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`bm-btn bm-btn--sm ${outcomeCode === option.value ? 'bm-btn--primary' : 'bm-btn--secondary'}`}
                onClick={() => onOutcomeCodeChange(option.value)}
                title={option.label}
              >
                {option.value}
              </button>
            ))
          )}
        </div>
        {errors.outcomeCode && (
          <span className="bm-input-error-msg">{errors.outcomeCode}</span>
        )}
      </div>
      {resultError && (
        <span className="bm-input-error-msg">{resultError}</span>
      )}
      {resultSuccess && (
        <div style={{ color: 'var(--bm-accent-green)', fontWeight: 600 }}>
          {resultSuccess}
        </div>
      )}
      <Button
        type="submit"
        variant="success"
        loading={loading}
        icon={<PiCheckCircleDuotone size={16} />}
      >
Сохранить результат и завершить пари
      </Button>
    </form>
  );
}
